import { ApolloCache } from '@apollo/client';
import {
  AgentComplianceReviewDocumentDocument,
  useAgentComplianceReviewIgnorePlatformRequirementIssueMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  AgentComplianceReviewDocumentQuery,
  AgentComplianceReviewPlatformRequirementIssueInput,
  PlatformRequirementIssueStatus,
  ValidationReviewSource,
} from '@client/graphql/__generated__/types';
import { useCallback } from 'react';

interface UseAgentComplianceReviewIgnorePlatformRequirementIssueArgs {
  /** Share-link token (`?token=` query param). */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

export type IgnorePlatformRequirementIssueArgs = Omit<
  AgentComplianceReviewPlatformRequirementIssueInput,
  'token' | 'ingestionFolderUuid' | 'isHidden'
> & {
  ingestionDocumentId: string;
  isHidden: boolean;
};

interface UseAgentComplianceReviewIgnorePlatformRequirementIssueResult {
  loading: boolean;
}

const patchPlatformRequirementIssueStatus = (
  cache: ApolloCache<unknown>,
  input: AgentComplianceReviewPlatformRequirementIssueInput & {
    ingestionDocumentId: string;
  }
): void => {
  cache.updateQuery<AgentComplianceReviewDocumentQuery>(
    {
      query: AgentComplianceReviewDocumentDocument,
      variables: {
        input: {
          token: input.token,
          ingestionFolderUuid: input.ingestionFolderUuid,
          ingestionDocumentId: input.ingestionDocumentId,
        },
      },
    },
    (data) => {
      const detail = data?.agentComplianceReviewDocument;
      if (!detail) {
        return data;
      }

      return {
        ...data,
        agentComplianceReviewDocument: {
          ...detail,
          platformRequirementIssues: detail.platformRequirementIssues.map(
            (issue) =>
              issue.publicIssueId === input.publicIssueId
                ? {
                    ...issue,
                    status: input.isHidden
                      ? PlatformRequirementIssueStatus.IGNORED_BY_AGENT
                      : PlatformRequirementIssueStatus.OPEN,
                    ignoredBySource: input.isHidden
                      ? ValidationReviewSource.AGENT
                      : undefined,
                    ignoredByAgentName: undefined,
                    ignoredAt: input.isHidden ? new Date() : undefined,
                  }
                : issue
          ),
        },
      };
    }
  );
};

/**
 * Wraps the token-bound checklist mismatch ignore mutation. Binds the
 * share-link token + folder uuid so callers only pass the public issue id and
 * optional hidden state.
 *
 * Like validation suggestion ignores, this patches Apollo cache in place rather
 * than refetching the heavy document-detail query.
 */
export const useAgentComplianceReviewIgnorePlatformRequirementIssue = ({
  token,
  ingestionFolderUuid,
}: UseAgentComplianceReviewIgnorePlatformRequirementIssueArgs): [
  (args: IgnorePlatformRequirementIssueArgs) => Promise<void>,
  UseAgentComplianceReviewIgnorePlatformRequirementIssueResult,
] => {
  const [mutate, { loading }] =
    useAgentComplianceReviewIgnorePlatformRequirementIssueMutation();

  const ignore = useCallback(
    async (args: IgnorePlatformRequirementIssueArgs): Promise<void> => {
      // Never fire the write with empty-string fallback identifiers — a missing
      // token/folder is a not-ready state, not a valid request to send.
      if (
        token == null ||
        token === '' ||
        ingestionFolderUuid == null ||
        ingestionFolderUuid === ''
      ) {
        return;
      }
      const { ingestionDocumentId, ...mutationArgs } = args;
      await mutate({
        variables: {
          input: {
            token,
            ingestionFolderUuid,
            ...mutationArgs,
          },
        },
        update: (cache) =>
          patchPlatformRequirementIssueStatus(cache, {
            token,
            ingestionFolderUuid,
            ingestionDocumentId,
            ...mutationArgs,
          }),
      });
    },
    [mutate, token, ingestionFolderUuid]
  );

  return [ignore, { loading }];
};
